import { ticketService } from "@/api/services/ticketService";
import { Message } from "@/components/inbox/types";
import { RootState } from "@/store/store";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
    hasPrevious: boolean;
}

interface ReadState {
    agentReadAt: string | null;
    customerReadAt: string | null;
}

interface ConversationState {
    conversationsMappedByTicketId: Record<string, Message[]>;
    readStatByTicketId: Record<string, ReadState>;
    pagination: Record<string, PaginationInfo>;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
}

const initialState: ConversationState = {
    conversationsMappedByTicketId: {},
    readStatByTicketId: {},
    pagination: {},
    loading: false,
    loadingMore: false,
    error: null,
}

/** Map API type values to the frontend Message type union. */
function normalizeMessage(raw: any): Message {
    return {
        ...raw,
        type: raw.type === 'chat' ? 'message' : raw.type || 'message',
    };
}

export const fetchConversationsAsync = createAsyncThunk(
    'conversations/fetchConversationsAsync',
    async (params: { ticketId: string; page?: number; limit?: number }) => {
        const response = await ticketService.getConversationPaginated(params.ticketId, {
            page: params.page || 1,
            limit: params.limit || 5
        });
        return { ...response, ticketId: params.ticketId };
    }
)

export const fetchConversationsPageAsync = createAsyncThunk(
    'conversations/fetchConversationsPageAsync',
    async (params: { ticketId: string; page: number; limit?: number }) => {
        const response = await ticketService.getConversationPaginated(params.ticketId, {
            page: params.page,
            limit: params.limit || 5
        });
        return { ...response, ticketId: params.ticketId };
    }
)

export const conversationsSlice = createSlice({
    name: 'conversations',
    initialState,
    reducers: {
        addNewConverstation: (state, action: PayloadAction<{ ticketId: string, message: Message }>) => {
            if (action.payload?.message?.content?.length === 0) {
                return;
            }
            if (!state.conversationsMappedByTicketId[action.payload.ticketId]) {
                state.conversationsMappedByTicketId[action.payload.ticketId] = [];
            }
            // Skip if message with same ID already exists (prevents duplicates from real-time + API)
            const existing = state.conversationsMappedByTicketId[action.payload.ticketId];
            if (existing.some(msg => msg.id === action.payload.message.id)) {
                return;
            }
            state.conversationsMappedByTicketId[action.payload.ticketId] = [...existing, normalizeMessage(action.payload.message)];
        },
        updateConversationStatus: (state, action: PayloadAction<{ ticketId: string, dashboardGeneratedId: string | null, status: 'sent' | 'delivered' | 'read' | 'sending', id: string | null }>) => {
            if (state.conversationsMappedByTicketId[action.payload.ticketId] !== undefined) {
                if (action.payload.dashboardGeneratedId !== null) {
                    // update id and the status
                    const message = state.conversationsMappedByTicketId[action.payload.ticketId].find(message => message.dashboardGeneratedId === action.payload.dashboardGeneratedId);
                    if (message) {
                        message.id = action.payload.id;
                        message.status = action.payload.status;
                    }
                } else {
                    const message = state.conversationsMappedByTicketId[action.payload.ticketId].find(message => message.id === action.payload.id);
                    if (message) {
                        message.status = action.payload.status;
                    }
                }
            }
        },
        updateReadState: (state, action: PayloadAction<{ ticketId: string, customerReadAt?: string | null, agentReadAt?: string | null }>) => {
            const { ticketId, customerReadAt, agentReadAt } = action.payload;
            if (!state.readStatByTicketId[ticketId]) {
                state.readStatByTicketId[ticketId] = { agentReadAt: null, customerReadAt: null };
            }
            if (customerReadAt !== undefined) {
                state.readStatByTicketId[ticketId].customerReadAt = customerReadAt;
            }
            if (agentReadAt !== undefined) {
                state.readStatByTicketId[ticketId].agentReadAt = agentReadAt;
            }
        },
        prependMessages: (state, action: PayloadAction<{ ticketId: string, messages: Message[] }>) => {
            const { ticketId, messages } = action.payload;
            if (!state.conversationsMappedByTicketId[ticketId]) {
                state.conversationsMappedByTicketId[ticketId] = [];
            }
            state.conversationsMappedByTicketId[ticketId] = [
                ...messages,
                ...state.conversationsMappedByTicketId[ticketId]
            ];
        },
        setPagination: (state, action: PayloadAction<{ ticketId: string, pagination: PaginationInfo }>) => {
            state.pagination[action.payload.ticketId] = action.payload.pagination;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchConversationsAsync.pending, (state) => {
            state.loading = true;
        })
        builder.addCase(fetchConversationsAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        })
        builder.addCase(fetchConversationsAsync.fulfilled, (state, action) => {
            state.conversationsMappedByTicketId[action.payload.ticketId] = action.payload.data.map(normalizeMessage);
            state.pagination[action.payload.ticketId] = action.payload.pagination;
            if (action.payload.readState) {
                state.readStatByTicketId[action.payload.ticketId] = action.payload.readState;
            }
            state.loading = false;
        })
        builder.addCase(fetchConversationsPageAsync.pending, (state) => {
            state.loadingMore = true;
        })
        builder.addCase(fetchConversationsPageAsync.rejected, (state, action) => {
            state.loadingMore = false;
            state.error = action.error.message;
        })
        builder.addCase(fetchConversationsPageAsync.fulfilled, (state, action) => {
            const { ticketId, data, pagination, readState } = action.payload;
            if (!state.conversationsMappedByTicketId[ticketId]) {
                state.conversationsMappedByTicketId[ticketId] = [];
            }
            // Prepend older messages to the beginning
            state.conversationsMappedByTicketId[ticketId] = [
                ...data.map(normalizeMessage),
                ...state.conversationsMappedByTicketId[ticketId]
            ];
            state.pagination[ticketId] = pagination;
            if (readState) {
                state.readStatByTicketId[ticketId] = readState;
            }
            state.loadingMore = false;
        })
    }
})


export const selectIsTicketConversationsFetched = (ticketId: string) => (state: RootState) =>
    state.conversations.conversationsMappedByTicketId[ticketId] !== undefined;
export const selectConversationsByTicketId = (ticketId: string) => (state: RootState) =>
    state.conversations.conversationsMappedByTicketId[ticketId];
export const selectIsTicketConversationsLoading = () => (state: RootState) =>
    state.conversations.loading;
export const selectIsTicketConversationsLoadingMore = () => (state: RootState) =>
    state.conversations.loadingMore;
export const selectIsTicketConversationsError = () => (state: RootState) =>
    state.conversations.error;
export const selectPaginationByTicketId = (ticketId: string) => (state: RootState) =>
    state.conversations.pagination[ticketId];
export const selectReadStateByTicketId = (ticketId: string) => (state: RootState) =>
    state.conversations.readStatByTicketId[ticketId];

export const { addNewConverstation, updateConversationStatus, updateReadState, prependMessages, setPagination } = conversationsSlice.actions;

export const conversationsReducer = conversationsSlice.reducer;